import React, { useEffect, useState, useRef } from 'react';
import { findDOMNode } from 'react-dom';
import Styles from 'global/styles';
import { get, set, sortBy } from 'global/lib/lodash';
import { useWindowSize } from 'global/hooks/useWindowSize';
import clsx from 'clsx';
import ModalGateway from 'global/components/ModalGateway';
import { CircularProgress, Icon, IconButton } from '@mui/material';
import { HorizontalScroll } from '../horizontal-scroll';
import { stopProp, _size } from '../../utils';
import { Button } from '../Button';
import CheckBoxOutlineBlankOutlined from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import Image from 'mui-image';

const ProcessingIcon = () => {
  const styles = Styles();

  return (
    <div className={styles.processingContainer}>
      <CircularProgress />
      <div className="processText">Processing Image...</div>
    </div>
  );
};

const EImage = (props) => {
  const contRef = useRef();
  const styles = Styles();
  const unsetHeight = get(props, 'unsetHeight', false);
  const unsetWidth = get(props, 'unsetWidth', false);
  const [dims, setDims] = useState();
  const [src, setSrc] = useState();
  const [isProcessing, setIsProcessing] = useState(false);
  const ref = useRef({
    error: '',
    errorCount: 0,
    setBackup: false,
    timeout: null,
  });
  let width = unsetWidth ? null : get(props, 'width', 110);
  let height = unsetHeight ? null : get(props, 'height', width);

  const autoReload = get(props, 'autoReload', true);
  const autosize = get(props, 'autosize', 1);
  const useNaturalDims = get(props, 'useNaturalDims', true);
  const canSelect = get(props, 'canSelect', false);
  const imageSel = get(props, 'imageSel', false);

  const windowDims = useWindowSize();

  const setRef = (sel, setting) => {
    set(ref, `current.${sel}`, setting);
  };

  const getRef = (sel, fb) => {
    return get(ref, `current.${sel}`, fb);
  };

  const calc = (force = false) => {
    if (autosize || force) {
      const node = findDOMNode(contRef.current);
      if (!node) {
        return;
      }
      const parent = node.parentNode;
      const innerWidth = parent.clientWidth * (autosize || 1);
      const img = node.getElementsByTagName('img')[0];
      if (img) {
        const imageMult = img.clientHeight / img.clientWidth;

        if (useNaturalDims) {
          setDims({
            width: unsetWidth ? null : img.naturalWidth,
            height: unsetHeight ? null : img.naturalHeight,
          });
        } else {
          setDims({
            width: unsetWidth ? null : innerWidth,
            height: unsetHeight ? null : innerWidth * (isNaN(imageMult) ? 1 : imageMult),
          });
        }
      }
    }
  };

  const startLoadCheck = () => {
    if (!getRef('timeout')) {
      const srcCopy = src;
      setSrc('');
      setIsProcessing(true);
      setRef(
        'timeout',
        setTimeout(() => {
          setRef('timeout', null);
          setSrc(srcCopy);
        }, 5000)
      );
    }
  };

  const onError = () => {
    setRef('errorCount', getRef('errorCount') + 1);
    const errorCount = getRef('errorCount');
    if (props.backupSrc && !getRef('setBackup')) {
      setRef('setBackup', true);
      setSrc(props.backupSrc);
    } else if (autoReload && errorCount < 10) {
      startLoadCheck();
    } else {
      get(props, 'onError', () => 0)();
    }
  };

  const selectImage = (e) => {
    stopProp(e);
    props.selectImage && props.selectImage(props);
  };

  const onLoad = () => {
    setTimeout(() => calc(), 500);
    setIsProcessing(false);
  };

  useEffect(() => {
    setSrc(props.src);
  }, [props.src]);

  useEffect(() => {
    calc();
  }, [autosize, windowDims.windowSize]);

  const hasCalced = !autosize ? true : !!dims || isProcessing;

  return (
    <div
      ref={contRef}
      className={clsx('IMAGE', styles.imageWrap, props.className)}
      style={{ ...(dims ? { ...dims } : { width, height }), ...(props.style || {}), ...(hasCalced ? { opacity: 1 } : { opacity: 0 }) }}
    >
      <Image {...props} imageStyle={(unsetHeight || unsetWidth) && { position: 'inherit' }} src={src} onLoad={onLoad} onError={onError} loading={isProcessing ? <ProcessingIcon /> : undefined} />
      {canSelect && (
        <IconButton
          onClick={selectImage}
          className={clsx(styles.checkIcon, imageSel && styles.checkIconSelected)}
          size="large">
          {imageSel ? <CheckBoxOutlined /> : <CheckBoxOutlineBlankOutlined />}
        </IconButton>
      )}
    </div>
  );
};

export default EImage;

export const ImageList = (props) => {
  const { printView, srcOverride, unsetHeight, unsetWidth } = props;
  const styles = Styles();
  const [modal, setModal] = useState(false);

  const imageSize = get(props, 'imageSize', printView ? 335 : 80);

  const images = sortBy(props.images || [], 'orderColumn');
  const displayAsLink = get(props, 'displayAsLink', false);

  const clickImage = (e, img, index) => {
    if (props.onClick) {
      return props.onClick(e);
    }
    e.stopPropagation();
    e.preventDefault();
    setModal({ index });
  };

  useEffect(() => {
    if (!!modal) {
      props.onOpen && props.onOpen();
    } else {
      props.onClose && props.onClose();
    }
  }, [modal]);

  const spaceBetween = get(props, 'spaceBetween', printView ? 8 : 0);

  const getSrc = (() => {
    if (srcOverride) {
      return srcOverride;
    } else {
      const hasLarge = get(images, '0.largeUrl');
      const hasThumb = get(images, `0.thumbUrl`);
      const hasUrl = get(images, '0.url');
      return { full: hasLarge ? 'largeUrl' : 'fullUrl', thumb: hasThumb ? 'thumbUrl' : hasUrl ? 'url' : 'fullUrl' };
    }
  })();

  return (
    <>
      {!displayAsLink &&
        images.map((img, i) => {
          return (
            <div key={i} style={{ marginRight: spaceBetween }} className={clsx(styles.imageListItem, props.style)}>
              <EImage
                {...img}
                className={styles.clickableImage}
                autosize={false}
                onClick={(e) => clickImage(e, img, i)}
                key={i}
                width={unsetWidth ? undefined : imageSize}
                height={unsetHeight ? undefined : imageSize}
                src={printView ? get(img, getSrc.full) : get(img, getSrc.thumb)}
                unsetHeight={unsetHeight}
                unsetWidth={unsetWidth}
              />
              {img.caption && props.showCaption && typeof img.caption === 'string' && (
                <div className={clsx(styles.imgCaption, 'caption')} style={{ width: imageSize, fontSize: printView ? 16 : undefined }}>
                  {img.caption}
                </div>
              )}
            </div>
          );
        })}

      {displayAsLink && !!_size(images) && (
        <Button
          className={clsx(styles.linkSelect)}
          size="small"
          bgColor={'rgba(255,255,255,0.0)'}
          variant={undefined}
          startIcon={<Icon>photo_library</Icon>}
          onClick={(e) => clickImage(e, images[0], 0)}
        >
          {`${_size(images)} Image${_size(images) > 1 ? 's' : ''}`}
        </Button>
      )}

      <ModalGateway
        open={modal}
        onClose={() => setModal()}
        currentIndex={get(modal, 'index')}
        images={images.map((x) => ({
          ...x,
          key: x.id,
          src: get(x, getSrc.full),
          caption: props.caption ? props.caption(x) : x.caption || x.name,
        }))}
      />
    </>
  );
};

export const ImageCarousel = (props) => {
  const styles = Styles();
  const [modal, setModal] = useState(false);

  const images = sortBy(props.images || [], 'orderColumn');

  const clickImage = (e, img, index) => {
    if (props.onClick) {
      return props.onClick(e);
    }
    e.stopPropagation();
    e.preventDefault();
    setModal({ index });
  };

  useEffect(() => {
    if (!!modal) {
      props.onOpen && props.onOpen();
    } else {
      props.onClose && props.onClose();
    }
  }, [modal]);

  const getSrc = get(props, 'getSrc', (img) => get(img, 'full', img.thumb));

  const Source = (img) => {
    const hasLarge = get(img, 'largeUrl');
    const hasThumb = get(img, `thumbUrl`);
    const hasUrl = get(img, 'url');

    let avail = {
      full: hasLarge || hasUrl,
      thumb: hasThumb || hasUrl,
    };

    return getSrc(avail);
  };

  return (
    <div className={styles.imageCarousel}>
      <HorizontalScroll useImageScroller slidesToSlide={1}>
        {images.map((img, i) => {
          return (
            <div key={i} className={clsx('imageItem', props.style)}>
              <EImage key={i} className={styles.clickableImage} onClick={(e) => clickImage(e, img, i)} src={Source(img)} useNaturalDims={false} {...props} />
            </div>
          );
        })}
      </HorizontalScroll>

      <ModalGateway
        open={modal}
        onClose={() => setModal()}
        currentIndex={get(modal, 'index')}
        images={images.map((x) => ({
          ...x,
          key: x.id,
          src: Source(x),
          caption: props.caption ? props.caption(x) : x.caption || x.name,
        }))}
      />
    </div>
  );
};
